import React from "react"
import { useTranslation } from "react-i18next"

import { useClipboard } from "shared/lib/clipboard"
import { useEvent } from "shared/lib/useEvent"
import { ProductInformation } from "shared/types/__generated__/graphql"
import { ClipboardButton } from "shared/ui/clipboard-button/ClipboardButton"
import { Readonly } from "shared/ui/readonly/Readonly"

import "./styles/_variant-key-display.scss"

interface AddonVariantKeyRowProps {
  addon: Pick<ProductInformation, "title" | "variantKey" | "productType">
  titleByProductType?: Record<string, string>
}

function AddonVariantKeyRow({
  addon,
  titleByProductType
}: AddonVariantKeyRowProps) {
  const { t } = useTranslation()
  const { copy } = useClipboard({
    successMessage: t("General.ItemCopied", {
      item: t("Product.VariantKey")
    })
  })

  const handleCopy = useEvent(() => copy(addon?.variantKey ?? ""))

  return (
    <Readonly.Root>
      <Readonly.Label>
        {titleByProductType?.[addon?.productType] || addon?.title}
      </Readonly.Label>
      <Readonly.Value>
        <span className="variant-key-display__variant-key">
          {addon?.variantKey}
        </span>
        <ClipboardButton
          onClick={handleCopy}
          label={t("General.CopyItem", {
            item: t("Product.VariantKey")
          })}
        />
      </Readonly.Value>
    </Readonly.Root>
  )
}

export interface VariantKeyDisplayProps {
  title?: string
  variantKey?: string
  addons?:
    | Pick<ProductInformation, "title" | "variantKey" | "productType">[]
    | null
  titleByProductType?: Record<string, string>
}

export function VariantKeyDisplay({
  title,
  variantKey,
  addons,
  titleByProductType
}: VariantKeyDisplayProps) {
  const { t } = useTranslation()
  const { copy } = useClipboard({
    successMessage: t("General.ItemCopied", {
      item: t("Product.VariantKey")
    })
  })

  const handleCopyVariantKeyToClipboard = useEvent(() => {
    return copy(variantKey ?? "")
  })

  if ((addons?.length ?? 0) === 0) {
    return (
      <Readonly.Value className="variant-key-display">
        <span className="variant-key-display__variant-key">{variantKey}</span>
        <ClipboardButton
          onClick={handleCopyVariantKeyToClipboard}
          label={t("General.CopyItem", {
            item: t("Product.VariantKey")
          })}
        />
      </Readonly.Value>
    )
  }

  return (
    <Readonly.Value className="variant-key-display">
      <Readonly.Root>
        <Readonly.Label>{title}</Readonly.Label>
        <Readonly.Value>
          <span className="variant-key-display__variant-key">{variantKey}</span>
          <ClipboardButton
            onClick={handleCopyVariantKeyToClipboard}
            label={t("General.CopyItem", {
              item: t("Product.VariantKey")
            })}
          />
        </Readonly.Value>
      </Readonly.Root>

      {addons?.map(addon => (
        <AddonVariantKeyRow
          key={addon.variantKey}
          addon={addon}
          titleByProductType={titleByProductType}
        />
      ))}
    </Readonly.Value>
  )
}
